import React, { useRef, useState, useContext } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from '../../utils/routeUtils';
import { Upload, FileText, CheckCircle, ArrowRight, Download, Trash2, RotateCcw, Eye, FileCheck, AlertCircle, X } from 'lucide-react';

const AttendanceApiPage = () => {
    const { roles } = useContext(AuthContext);
    const navigate = useNavigate();
    const fileInputRef = useRef(null)
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingConfirm, setLoadingConfirm] = useState(false);
    const [notification, setNotification] = useState(null);
    const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'preview', 'confirmed'
    const token = getToken();
    const [isDragOver, setIsDragOver] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [uploadId, setUploadId] = useState(null);
    const [uploadResponse, setUploadResponse] = useState(null);

    const hasPreviewRows = Array.isArray(previewData?.preview_rows) && previewData.preview_rows.length > 0;

    const getRoleBasedRouteLocal = (route) => {
        return getRoleBasedRoute(route, roles);
    };

    const processSelectedFile = (selectedFile) => {
        if (!selectedFile) return;
        const isTxt = selectedFile.name.endsWith('.txt');
        const isSizeValid = selectedFile.size <= 5 * 1024 * 1024; // 5MB

        if (!isTxt) {
            setNotification({ message: "Only .txt files are allowed.", type: "error" });
            if (fileInputRef.current) fileInputRef.current.value = null;
            return;
        }

        if (!isSizeValid) {
            setNotification({ message: "File size must be 5MB or less.", type: "error" });
            if (fileInputRef.current) fileInputRef.current.value = null;
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            setFilePreview(content.split('\n'));
        };
        reader.readAsText(selectedFile);

        setFile(selectedFile);
        setPreviewData(null);
        setUploadId(null);
    };

    const handleFileChange = (e) => {
        const selected = e.target.files && e.target.files[0];
        processSelectedFile(selected);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const droppedFile = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        processSelectedFile(droppedFile);
    };

    const uploadAttendance = async () => {
        setLoadingPreview(true);
        if (!file) {
            setNotification({ message: "Please select a file first.", type: "error" });
            setLoadingPreview(false);
            return;
        }
        
        const formData = new FormData();
        formData.append("attendance_file", file);
    
        try {
            const response = await axios.post("https://hrmspayroll.prepstripe.com/api/attendance-uploads/preview", formData, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                }
            });
            setPreviewData(response.data);
            setUploadId(response.data.token);
            setCurrentStep('preview');
            setNotification({
                message: response.data.summary_message || "File processed. Review data before confirming.",
                type: "success"
            });
        } catch (error) {
            console.error("Error processing attendance file:", error);
            setNotification({
                message: error.response?.data?.message || "Failed to process file.",
                type: "error"
            });
        } finally {
            setLoadingPreview(false);
        }
    };

    const confirmUpload = async () => {
        if (!uploadId) return;

        setLoadingConfirm(true);
        try {
            const response = await axios.post("https://hrmspayroll.prepstripe.com/api/attendance-uploads/confirm", {
                token: uploadId
            }, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                }
            });
            
            setUploadResponse(response.data);
            setCurrentStep('confirmed');
            setNotification({
                message: "Attendance data saved successfully!",
                type: "success"
            });
        } catch (error) {
            console.error("Error confirming attendance upload:", error);
            setNotification({
                message: error.response?.data?.message || "Failed to confirm upload.",
                type: "error"
            });
        } finally {
            setLoadingConfirm(false);
        }
    };

    const resetFileSelection = () => {
        if (fileInputRef.current) fileInputRef.current.value = null;
        setFile(null);
        setFilePreview(null);
        setUploadResponse(null);
        setPreviewData(null);
        setUploadId(null);
        setCurrentStep('upload');
    };

    const handleDownload = async () => {
        try {
            const response = await fetch("/hrmsAttend.txt");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "hrmsAttend_sample.txt";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    const StepIcon = ({ step, current }) => {
        const isActive = step === current;
        const isCompleted = (current === 'preview' && step === 'upload') || current === 'confirmed';
        
        return (
            <div className={`flex items-center gap-3 transition-all duration-300 ${isActive ? 'scale-110' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-lg transition-all duration-300 ${
                    isCompleted ? 'bg-emerald-500 text-white shadow-emerald-200' :
                    isActive ? 'bg-blue-600 text-white shadow-blue-200 ring-4 ring-blue-50' :
                    'bg-gray-100 text-gray-400'
                }`}>
                    {isCompleted ? <CheckCircle size={20} /> : step === 'upload' ? '1' : '2'}
                </div>
                <span className={`font-semibold ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                    {step === 'upload' ? 'Upload' : 'Preview'}
                </span>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] dark:bg-gray-950 font-poppins">
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <nav className="mb-8" aria-label="Breadcrumb">
                    <ol className="flex items-center space-x-3 text-sm">
                        <li>
                            <Link to={getRoleBasedRouteLocal("/admin/attendance_management")} 
                                  className="text-gray-500 hover:text-blue-600 transition-colors">
                                Attendance Management
                            </Link>
                        </li>
                        <li className="flex items-center gap-3 text-gray-400">
                            <ArrowRight size={14} />
                            <span className="text-gray-900 font-medium dark:text-white">Upload Attendance</span>
                        </li>
                    </ol>
                </nav>

                {notification && (
                    <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
                )}

                {/* Header Section */}
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 mb-8 flex flex-col md:flex-row items-center justify-between gap-6">
                    {/* Stepper */}
                    <div className="flex items-center gap-8">
                        <StepIcon step="upload" current={currentStep} />
                        <div className="w-16 h-[2px] bg-gray-100 dark:bg-gray-800 rounded-full">
                            <div className={`h-full bg-emerald-500 rounded-full transition-all duration-500 ${currentStep !== 'upload' ? 'w-full' : 'w-0'}`} />
                        </div>
                        <StepIcon step="preview" current={currentStep} />
                    </div>

                    <button
                        onClick={handleDownload}
                        className="group flex items-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white px-6 py-3 rounded-2xl transition-all duration-300 font-semibold shadow-sm"
                    >
                        <Download size={18} className="group-hover:translate-y-0.5 transition-transform" />
                        Download Sample File
                    </button>
                </div>

                {/* Main Content */}
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {currentStep === 'upload' && (
                        <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-xl shadow-gray-200/50 dark:shadow-none overflow-hidden border border-gray-100 dark:border-gray-800">
                            <div className="p-10">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Upload Attendance File</h2>
                                
                                <input
                                    type="file"
                                    accept=".txt"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                />

                                <div
                                    className={`relative group cursor-pointer rounded-[2rem] border-2 border-dashed p-16 transition-all duration-300 flex flex-col items-center justify-center gap-4 ${
                                        isDragOver 
                                        ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' 
                                        : 'border-gray-200 dark:border-gray-800 hover:border-blue-300 hover:bg-gray-50/50'
                                    }`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <div className={`w-20 h-20 rounded-3xl flex items-center justify-center transition-all duration-300 ${isDragOver ? 'bg-blue-100 text-blue-600 scale-110' : 'bg-gray-100 text-gray-400 dark:bg-gray-800 group-hover:bg-blue-50 group-hover:text-blue-500'}`}>
                                        <Upload size={32} />
                                    </div>
                                    
                                    <div className="text-center">
                                        {!file ? (
                                            <>
                                                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    Drag & drop your <span className="text-blue-600 font-bold">.txt</span> file here
                                                </p>
                                                <p className="text-gray-500 dark:text-gray-400 mt-2">or click to browse from your device</p>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-xl">
                                                    <FileText size={20} className="text-blue-600" />
                                                    <span className="text-blue-700 dark:text-blue-300 font-bold">{file.name}</span>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); resetFileSelection(); }}
                                                        className="p-1 hover:bg-red-100 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                                <p className="text-gray-400 text-sm">{(file.size / 1024).toFixed(1)} KB</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-6">
                                    <p className="text-sm font-medium text-gray-400 flex items-center gap-2">
                                        <AlertCircle size={14} className="text-blue-500" />
                                        Only .txt files. Max size: 5MB.
                                    </p>
                                    
                                    {file && (
                                        <div className="flex items-center gap-4 w-full md:w-auto">
                                            <button
                                                onClick={resetFileSelection}
                                                className="flex-1 md:flex-none flex items-center justify-center gap-2 text-gray-500 font-bold hover:text-gray-900 transition-colors uppercase tracking-widest text-xs px-6"
                                            >
                                                <Trash2 size={16} />
                                                Reset
                                            </button>
                                            <button
                                                onClick={uploadAttendance}
                                                disabled={loadingPreview}
                                                className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-bold shadow-lg shadow-blue-200 transition-all duration-300 group disabled:opacity-50"
                                            >
                                                {loadingPreview ? 'PROCESSING...' : (
                                                    <>
                                                        PROCESS FILE
                                                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {filePreview && (
                                <div className="border-t border-gray-100 dark:border-gray-800 bg-[#FBFDFF] dark:bg-black/20 p-10">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-sm font-bold text-gray-500 uppercase tracking-[0.2em] flex items-center gap-2">
                                            <Eye size={16} className="text-blue-500" />
                                            File Preview Content
                                        </h3>
                                        <span className="text-xs bg-white dark:bg-gray-800 px-3 py-1 rounded-full text-gray-400 font-medium border border-gray-100 dark:border-gray-700 shadow-sm">
                                            {filePreview.length} lines detected
                                        </span>
                                    </div>
                                    <div className="bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 p-6 rounded-3xl max-h-[300px] overflow-y-auto font-mono text-sm shadow-inner">
                                        {filePreview.map((line, index) => (
                                            <div key={index} className="flex gap-4 py-1.5 border-b border-gray-50 dark:border-gray-900 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-900/50 transition-colors group">
                                                <span className="text-gray-300 dark:text-gray-700 w-8 select-none group-hover:text-blue-300 transition-colors">{index + 1}</span>
                                                <span className="text-gray-600 dark:text-gray-400 break-all">{line || <span className="text-gray-200 italic">empty line</span>}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {currentStep === 'preview' && previewData && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
                            {/* Summary Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    { label: 'Unique Entries', value: previewData.counts?.unique_employees, color: 'blue' },
                                    { label: 'Duplicate Entries', value: previewData.counts?.duplicate_days, color: 'emerald' },
                                    { label: 'Skipped IDs', value: previewData.counts?.skipped_emp_ids, color: 'amber' },
                                    { label: 'Timestamp Issues', value: previewData.counts?.lines_bad_ts, color: 'rose' }
                                ].map((stat, i) => (
                                    <div key={i} className="bg-white dark:bg-gray-900 p-8 rounded-[2rem] shadow-sm border border-gray-100 dark:border-gray-800">
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                                            {stat.label}
                                        </p>
                                        <p className={`text-4xl font-black text-${stat.color}-600`}>
                                            {stat.value || 0}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {/* Note Section */}
                            <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] p-10 shadow-sm border border-gray-100 dark:border-gray-800">
                                <div className="flex items-start gap-6 bg-blue-50/50 dark:bg-blue-900/10 p-8 rounded-3xl border border-blue-100 dark:border-blue-900/30">
                                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                                        <FileCheck size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 tracking-tight">System Analysis Note</h3>
                                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
                                            {previewData.note || "System has analyzed the file. Please confirm the summary above to persist data to the database."}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-12 flex flex-col sm:flex-row items-center justify-end gap-6">
                                    <button
                                        onClick={resetFileSelection}
                                        className="w-full sm:w-auto flex items-center justify-center gap-2 text-rose-500 font-bold hover:text-rose-600 transition-colors uppercase tracking-[0.2em] text-[10px]"
                                    >
                                        <RotateCcw size={16} />
                                        RE-UPLOAD NEW FILE
                                    </button>
                                    <div className="h-8 w-[1px] bg-gray-100 dark:bg-gray-800 hidden sm:block" />
                                    <button
                                        onClick={confirmUpload}
                                        disabled={loadingConfirm || !hasPreviewRows}
                                        className="w-full sm:w-auto flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-5 rounded-2xl font-bold shadow-xl shadow-emerald-200 transition-all duration-300 disabled:opacity-50 transform hover:scale-[1.02] active:scale-[0.98]"
                                    >
                                        {loadingConfirm ? 'SAVING DATA...' : (
                                            <>
                                                CONFIRM & SAVE DATA
                                                <CheckCircle size={20} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 'confirmed' && uploadResponse && (
                        <div className="max-w-3xl mx-auto py-20 text-center animate-in zoom-in duration-500">
                            <div className="w-24 h-24 bg-emerald-100 text-emerald-600 rounded-[2rem] flex items-center justify-center mx-auto mb-10 shadow-2xl shadow-emerald-200 relative">
                                <CheckCircle size={48} strokeWidth={3} />
                                <div className="mt-8">
                                    <span className="absolute -top-1 -right-1 flex h-6 w-6">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-6 w-6 bg-emerald-500"></span>
                                    </span>
                                </div>
                            </div>
                            
                            <h2 className="text-4xl font-black text-gray-900 dark:text-white mb-4 tracking-tight">Upload Successful!</h2>
                            <p className="text-xl text-gray-500 dark:text-gray-400 mb-12 font-medium max-w-lg mx-auto leading-relaxed">
                                {uploadResponse.insertedCount 
                                    ? `Processed ${uploadResponse.insertedCount} attendance records successfully into the database.`
                                    : 'Your attendance records have been synchronized and are ready for review.'}
                            </p>
                            
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <button
                                    onClick={resetFileSelection}
                                    className="w-full sm:w-auto flex items-center justify-center gap-3 bg-white dark:bg-gray-900 text-blue-600 border-2 border-blue-50 hover:border-blue-100 px-8 py-4 rounded-2xl font-bold transition-all duration-300 shadow-sm"
                                >
                                    <RotateCcw size={18} />
                                    Upload Another
                                </button>
                                <button
                                    onClick={() => navigate(getRoleBasedRouteLocal("/admin/attendance_management/check-attendance/"))}
                                    className="w-full sm:w-auto flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-bold transition-all duration-300 shadow-lg shadow-blue-200"
                                >
                                    Check Records
                                    <ArrowRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttendanceApiPage;